'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  editApplicationApi,
  getAllApplications,
  getApplicant,
  type ApplicationType,
  type Listing,
  type ScheduledUnitsType,
} from '@repo/api';
import { Button, Divider, TextBadge, type ApplicationStatus } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ChatDetail } from '@/components/chat/ChatDetail';
import { useStore } from '@/providers/StoreProvider';
import { useRouter } from 'next/navigation';
import {
  AUTH_MESSAGES,
  bathroomLabel,
  CHAT_TOUR_TYPES,
  getUnitTourHighlight,
  type ChatTourType,
} from '@repo/utils';
import { useWithAuth } from '@/hooks/useWithAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ListingPromotionPrice } from '@/features/listings/PriceDetailsTrigger';
import { UnitTourHighlightCard } from './UnitTourHighlightCard';
import { urgencyParam } from '@/lib/listings/urgency';
import { isTourAutoTriggerEmailSource } from '../emailSource';
import { ApplicationLimitDialog } from '@/components/applications/ApplicationLimitDialog';
import { EditApplicationModal } from '@/app/profile/applications/components/EditApplicationModal';
import {
  countUnitDrafts,
  isAtCombinedApplicationLimit,
  findApplicationForUnit,
  isAtActiveApplicationLimit,
  isDraftStatus,
  resolveActiveApplicationLimit,
} from '@/lib/applications/limit';
import { needsEditConfirmation, normalizeApplicationStatus } from '@/lib/applications/status';
import { buildApplicationProfileUrl } from '@/app/profile/application-profile/common/navigation';

interface UnitActionButtonsProps {
  listing: Listing;
  mobileOnly?: boolean;
  autoTriggerScheduleTour?: boolean;
}

export function UnitActionButtons({
  listing,
  mobileOnly = false,
  autoTriggerScheduleTour = false,
}: UnitActionButtonsProps) {
  const router = useRouter();
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Which call to action opened the chat: the sidebar routes both the tour
  // highlight card and the "Schedule a tour" button through one handler, and the
  // backend opens with a different script for each.
  const [chatTourType, setChatTourType] = useState<ChatTourType>(CHAT_TOUR_TYPES.SCHEDULE_TOUR);
  const hasAutoTriggeredRef = useRef(false);
  // Which application-limit dialog is showing, if any. Resolved on click (see
  // handleApply) rather than on page load: this is a high-traffic SEO route and
  // must not make an authenticated request per view, and an on-click read can
  // never be stale.
  const [limitDialog, setLimitDialog] = useState<
    | { kind: 'reached'; activeCount: number; limit: number; status: ApplicationStatus }
    | { kind: 'reminder'; limit: number; status: ApplicationStatus }
    | { kind: 'edit'; application: ApplicationType; status: ApplicationStatus }
    | null
  >(null);
  const [isResolvingApply, setIsResolvingApply] = useState(false);
  const [isRevertingToDraft, setIsRevertingToDraft] = useState(false);
  const scheduledUnits = useStore(s => s.chat.scheduledUnits);
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const sessionHydrated = useStore(state => state.webAuth.sessionHydrated);
  const isLoading = useStore(s => s.chat.isFetchingChatList);
  const setCurrentUserScheduledUnits = useStore(s => s.chat?.setCurrentUserScheduledUnits);
  const withAuth = useWithAuth();
  const analytics = useAnalytics();
  const hasUserScheduledTour = useMemo(
    () => scheduledUnits.some((u: ScheduledUnitsType) => u.unit.slug === listing.slug),
    [scheduledUnits, listing.slug],
  );
  const grossRent =
    listing.activeListing?.grossRent ??
    listing.activeListing?.rent ??
    listing.previousListingRent ??
    0;
  const hasPrice = grossRent > 0;

  const numBedrooms = listing.numBedrooms ?? 0;
  const bedroomLabel =
    numBedrooms === 0 ? 'Studio' : numBedrooms === 1 ? '1 bed' : `${numBedrooms} beds`;
  const bathroomText = bathroomLabel(listing.numBathrooms ?? 0, listing.numHalfBathrooms ?? 0);
  const applicationUrl = `/profile/application-profile?tab=basic-info&substep=PHI0&unit=${encodeURIComponent(
    listing.slug,
  )}`;
  const addressForAnalytics = listing.fullAddress?.split(',')?.[0] ?? listing.addressSlug;
  // The dialogs name the unit the way the design does ("402 E 92nd St #11C"):
  // street + unit only, without the city/state/zip tail.
  const applyDialogAddress = addressForAnalytics;
  const hasActiveListing = Boolean(listing.activeListing);
  const showApply = Boolean(listing.isApplicationsAllowed && hasActiveListing);

  const clearAutoTriggerEmailSource = useCallback(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!isTourAutoTriggerEmailSource(url.searchParams.get('email_source'))) return;
    url.searchParams.delete('email_source');
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  }, []);

  const handleClose = useCallback(() => {
    setIsChatOpen(false);
    void setCurrentUserScheduledUnits?.();
  }, [setCurrentUserScheduledUnits]);

  /**
   * Apply, gated by the active-application limit.
   *
   * Everything below runs INSIDE `withAuth`, so it only ever executes for a
   * signed-in renter — an anonymous click logs in first and lands here after.
   *
   * Precedence, from the Figma matrix (limit state x per-unit state):
   *   1. a SUBMITTED application already exists for this unit -> the edit
   *      interstitial, even at the limit: that application already occupies one
   *      of the slots, and editing it reverts it to a draft, freeing one.
   *   2. at the limit -> hard stop, including when a draft exists for this unit
   *      (a draft would only become an eleventh active application).
   *   3. a draft exists for this unit -> resume it silently, as today.
   *   4. drafts + active at the limit (but active still under it) -> soft
   *      reminder, which proceeds. Drafts themselves are never capped.
   *   5. otherwise -> straight into the flow.
   *
   * Fails open: if either request fails we navigate as before. The backend's
   * submit guard is the real ceiling, so a missed dialog costs a late error
   * message, never a wrongly blocked renter.
   */
  const handleApply = useCallback(() => {
    void withAuth(async () => {
      void analytics.track('unit_apply_now', {
        unitId: listing.slug,
        addressSlug: listing.slug,
      });

      setIsResolvingApply(true);
      let applications: ApplicationType[] = [];
      let activeCount: number | undefined;
      let activeLimit: number | undefined;
      try {
        const [applicant, apps] = await Promise.all([getApplicant(), getAllApplications()]);
        activeCount = applicant?.activeApplicationCount;
        activeLimit = applicant?.activeApplicationLimit;
        applications = Array.isArray(apps) ? apps : [];
      } catch (error) {
        console.error('Could not resolve application limit; applying anyway', error);
        setIsResolvingApply(false);
        router.push(applicationUrl);
        return;
      }
      setIsResolvingApply(false);

      const existing = findApplicationForUnit(applications, listing.id);
      // The status every limit event reports. With no application for this unit
      // — the usual case for the reminder and hard-stop dialogs — this is
      // 'unknown', reported rather than coerced (same choice the existing
      // profile_application_* events make).
      const status = normalizeApplicationStatus(existing?.status);

      if (existing && needsEditConfirmation(status)) {
        setLimitDialog({ kind: 'edit', application: existing, status });
        return;
      }

      const limit = resolveActiveApplicationLimit(activeLimit);

      if (isAtActiveApplicationLimit(activeCount, activeLimit)) {
        setLimitDialog({ kind: 'reached', activeCount: activeCount ?? limit, limit, status });
        return;
      }

      if (existing && isDraftStatus(existing.status)) {
        router.push(applicationUrl);
        return;
      }

      // Drafts + active at the ceiling: they can't submit them all, so state the
      // limit before they start another. `active < limit` is already true here —
      // the hard stop above returned otherwise.
      if (isAtCombinedApplicationLimit(countUnitDrafts(applications), activeCount, activeLimit)) {
        setLimitDialog({ kind: 'reminder', limit, status });
        return;
      }

      router.push(applicationUrl);
    }, AUTH_MESSAGES.APPLICATION_PROFILE);
  }, [analytics, applicationUrl, listing.id, listing.slug, router, withAuth]);

  /**
   * "Continue to edit" — reverts the submitted application, then opens it.
   *
   * Routes by `editId`, exactly as the applications list does, rather than by
   * unit alone: the boot resolves a bare `?unit=` with a `find` over every
   * application, so an id pins the one the renter actually chose. Passing
   * `applicationType` too avoids the boot having to correct the URL afterwards,
   * which would otherwise flash the personal flow at a guarantor.
   */
  const handleContinueToEdit = useCallback(async () => {
    if (limitDialog?.kind !== 'edit' || !limitDialog.application.id) return;
    setIsRevertingToDraft(true);
    try {
      const next = await editApplicationApi(limitDialog.application.id);
      router.push(
        buildApplicationProfileUrl({
          tab: 'basic-info',
          substep: 'PHI0',
          unit: next.unitSlug ?? listing.slug,
          editId: next.id ?? limitDialog.application.id,
          applicationType: next.isSelfApplied === false ? 'guarantor' : 'personal',
        }),
      );
    } catch (error) {
      console.error('Could not revert the application to a draft', error);
      setIsRevertingToDraft(false);
      setLimitDialog(null);
    }
    // On success we're navigating away, so the busy flag intentionally stays
    // set — it keeps the dialog's actions disabled through the transition.
  }, [limitDialog, listing.slug, router]);

  const handleScheduleTour = useCallback(
    (tourType: ChatTourType = CHAT_TOUR_TYPES.SCHEDULE_TOUR) => {
      const isFuture = Boolean(listing.activeListing?.isFutureListing);
      const isVerified = Boolean(listing.isVerified);
      const chat_source = isFuture
        ? 'future'
        : isVerified
          ? 'current_verified'
          : 'current_non_verified';

      if (!isLoggedIn) {
        void analytics.track('schedule_tour_non_login', {
          unitId: listing.slug,
          address: addressForAnalytics,
          screen: 'unit',
          chat_source,
          ...urgencyParam(listing),
        });
      }

      // A video tour is "view", not "schedule"; open house and the plain
      // "Schedule a tour" button both stay on the scheduling copy.
      const authMessage =
        tourType === CHAT_TOUR_TYPES.VIDEO_TOUR
          ? AUTH_MESSAGES.VIDEO_TOUR
          : AUTH_MESSAGES.CHAT_MESSAGE;

      void withAuth(async () => {
        void analytics.track('schedule_tour_login', {
          unitId: listing.slug,
          address: addressForAnalytics,
          screen: 'unit',
          chat_type: hasUserScheduledTour ? 'continue' : 'new',
          chat_source,
          ...urgencyParam(listing),
        });
        setChatTourType(tourType);
        setIsChatOpen(true);
        clearAutoTriggerEmailSource();
      }, authMessage);
    },
    [
      addressForAnalytics,
      analytics,
      clearAutoTriggerEmailSource,
      hasUserScheduledTour,
      isLoggedIn,
      listing.activeListing,
      listing,
      withAuth,
    ],
  );

  useEffect(() => {
    if (
      !autoTriggerScheduleTour ||
      hasAutoTriggeredRef.current ||
      !hasActiveListing ||
      !sessionHydrated ||
      (isLoggedIn && isLoading)
    ) {
      return;
    }
    hasAutoTriggeredRef.current = true;
    handleScheduleTour();
  }, [
    autoTriggerScheduleTour,
    handleScheduleTour,
    hasActiveListing,
    isLoading,
    isLoggedIn,
    sessionHydrated,
  ]);

  return (
    <>
      <div
        className={cn(
          'fixed md:static bottom-0 left-0 right-0 z-50 md:z-auto bg-white shadow-md-reverse md:shadow md:rounded-3xl md:border md:border-tertiary',
          mobileOnly && 'md:hidden',
        )}
      >
        <div className="md:px-6 md:py-6 px-4 py-4">
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              {hasPrice ? (
                <>
                  <ListingPromotionPrice
                    listing={listing}
                    screen="unit"
                    grossClassName="text-sm"
                    netClassName={cn('text-xl font-bold text-primary')}
                  />
                  <span className="text-sm text-secondary">·</span>
                </>
              ) : null}
              <span className="text-sm text-secondary">
                {bedroomLabel}, {bathroomText}
              </span>
            </div>
          </div>

          {/* Open house / video tour card – desktop sidebar only */}
          {!mobileOnly && getUnitTourHighlight(listing) ? (
            <div className="space-y-1">
              <Divider variant="tertiary" />
              <UnitTourHighlightCard
                listing={listing}
                onClick={handleScheduleTour}
                containerStyle="!px-0 mb-2 border-none shadow-none"
              />
            </div>
          ) : null}
          <div className="flex flex-row sm:flex-col gap-2 sm:gap-4">
            {hasActiveListing ? (
              <>
                <Button
                  variant="primary"
                  className="flex-1 sm:w-full order-2 sm:order-1"
                  isLoading={isLoading}
                  onClick={() => {
                    handleScheduleTour();
                  }}
                >
                  {hasUserScheduledTour ? 'Continue chat' : 'Schedule a tour'}
                </Button>

                {showApply ? (
                  <Button
                    variant="secondary"
                    className="flex-1 sm:w-full order-1 sm:order-2"
                    disabled={isResolvingApply}
                    isLoading={isResolvingApply}
                    onClick={() => {
                      handleApply();
                    }}
                  >
                    Apply
                  </Button>
                ) : null}
              </>
            ) : (
              <TextBadge className="w-full">This apartment is no longer available.</TextBadge>
            )}
          </div>
        </div>
      </div>

      {/* Chat Messenger Modal */}
      {isChatOpen ? (
        <ChatDetail
          variant="overlay"
          open={isChatOpen}
          onClose={handleClose}
          listing={listing}
          tourType={chatTourType}
        />
      ) : null}

      {limitDialog?.kind === 'reached' ? (
        <ApplicationLimitDialog
          open
          variant="reached"
          limit={limitDialog.limit}
          activeCount={limitDialog.activeCount}
          actionLabel="View applications"
          onAction={() => {
            void analytics.track('unit_application_alert_view_applications', {
              application_status: limitDialog.status,
            });
            router.push('/profile/applications');
          }}
          onClose={() => setLimitDialog(null)}
        />
      ) : null}

      {limitDialog?.kind === 'reminder' ? (
        <ApplicationLimitDialog
          open
          variant="reminder"
          limit={limitDialog.limit}
          address={applyDialogAddress}
          onViewApplications={() => {
            void analytics.track('unit_application_warning_view_applications', {
              application_status: limitDialog.status,
            });
            router.push('/profile/applications');
          }}
          onStart={() => {
            void analytics.track('unit_application_warning_start', {
              application_status: limitDialog.status,
            });
            setLimitDialog(null);
            router.push(applicationUrl);
          }}
          onClose={() => setLimitDialog(null)}
        />
      ) : null}

      {limitDialog?.kind === 'edit' ? (
        <EditApplicationModal
          open
          address={applyDialogAddress}
          busy={isRevertingToDraft}
          onView={() => {
            void analytics.track('unit_application_warning_view', {
              application_status: limitDialog.status,
            });
            router.push(`/profile/applications/${limitDialog.application.id}/view`);
          }}
          onContinue={() => {
            void analytics.track('unit_application_warning_edit', {
              application_status: limitDialog.status,
            });
            void handleContinueToEdit();
          }}
          onClose={() => setLimitDialog(null)}
        />
      ) : null}
    </>
  );
}
